// Watermark (§11.15): text (system sans; size as % of the short edge; colour; opacity) or a logo PNG
// (transparency respected). Position on a 9-point grid plus a margin (% of the short edge). Scales with
// the output size. Applied after resize, in encoded space, on the quantized buffer (export worker).
export type WatermarkSettings = {
  kind: 'text' | 'logo';
  text: string;
  /** Text height (or the logo's longer side) as % of the output's short edge. */
  sizePct: number;
  color: [number, number, number];
  opacity: number; // 0..1
  /** 0..8, row-major: 0 = top-left, 4 = centre, 8 = bottom-right. */
  position: number;
  marginPct: number;
  /** Logo PNG bytes (stored in IndexedDB by the UI). */
  logo?: Uint8Array;
};

export const DEFAULT_WATERMARK: WatermarkSettings = {
  kind: 'text',
  text: '© EAT',
  sizePct: 4,
  color: [1, 1, 1],
  opacity: 0.6,
  position: 8,
  marginPct: 2,
};

/** Top-left of a w×h box at grid `position` inside W×H with a margin of `marginPct`% of the short edge. */
export function placeBox(
  W: number,
  H: number,
  w: number,
  h: number,
  position: number,
  marginPct: number,
): { x: number; y: number } {
  const m = Math.round((marginPct / 100) * Math.min(W, H));
  const col = position % 3,
    row = Math.floor(position / 3);
  const x = col === 0 ? m : col === 1 ? Math.round((W - w) / 2) : W - w - m;
  const y = row === 0 ? m : row === 1 ? Math.round((H - h) / 2) : H - h - m;
  return { x, y };
}

/** Renders the watermark into an RGBA8 bitmap sized for a W×H output (OffscreenCanvas 2D). */
async function renderMark(
  W: number,
  H: number,
  wm: WatermarkSettings,
): Promise<{ data: Uint8ClampedArray; w: number; h: number } | null> {
  const short = Math.min(W, H);
  const target = Math.max(1, Math.round((wm.sizePct / 100) * short));
  if (wm.kind === 'logo') {
    if (!wm.logo?.length) return null;
    const bmp = await createImageBitmap(
      new Blob([wm.logo as Uint8Array<ArrayBuffer>], { type: 'image/png' }),
      { premultiplyAlpha: 'none' },
    );
    const k = target / Math.max(bmp.width, bmp.height);
    const w = Math.max(1, Math.round(bmp.width * k)),
      h = Math.max(1, Math.round(bmp.height * k));
    const c = new OffscreenCanvas(w, h);
    const ctx = c.getContext('2d')!;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close();
    return { data: ctx.getImageData(0, 0, w, h).data, w, h };
  }
  if (!wm.text.trim()) return null;
  const font = `600 ${target}px system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
  const probe = new OffscreenCanvas(8, 8).getContext('2d')!;
  probe.font = font;
  const m = probe.measureText(wm.text);
  const w = Math.max(1, Math.ceil(m.width + target * 0.2));
  const h = Math.max(1, Math.ceil(target * 1.3));
  const c = new OffscreenCanvas(w, h);
  const ctx = c.getContext('2d')!;
  ctx.font = font;
  ctx.textBaseline = 'middle';
  ctx.fillStyle = `rgb(${wm.color.map((v) => Math.round(v * 255)).join(',')})`;
  ctx.fillText(wm.text, target * 0.1, h / 2);
  return { data: ctx.getImageData(0, 0, w, h).data, w, h };
}

/** Composites the watermark over an RGBA buffer (8- or 16-bit, encoded values, straight alpha). */
export async function applyWatermark(
  buf: Uint8Array | Uint16Array,
  W: number,
  H: number,
  bitDepth: 8 | 16,
  wm: WatermarkSettings,
) {
  const mark = await renderMark(W, H, wm);
  if (!mark) return;
  const { x: ox, y: oy } = placeBox(W, H, mark.w, mark.h, wm.position, wm.marginPct);
  const max = bitDepth === 16 ? 65535 : 255;
  for (let y = 0; y < mark.h; y++) {
    const dy = oy + y;
    if (dy < 0 || dy >= H) continue;
    for (let x = 0; x < mark.w; x++) {
      const dx = ox + x;
      if (dx < 0 || dx >= W) continue;
      const si = (y * mark.w + x) * 4;
      const a = (mark.data[si + 3]! / 255) * wm.opacity;
      if (a <= 0) continue;
      const di = (dy * W + dx) * 4;
      const ad = buf[di + 3]! / max;
      const ao = a + ad * (1 - a);
      for (let c = 0; c < 3; c++) {
        const s = (mark.data[si + c]! / 255) * max;
        buf[di + c] = Math.round((s * a + buf[di + c]! * ad * (1 - a)) / ao);
      }
      buf[di + 3] = Math.round(ao * max);
    }
  }
}
